import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
  Tabs as BaseTabs,
  Tab,
  TabPanel,
} from "Components/Base/Navigation/Tab";

/**
 * Tabs for showing different content.
 * This component exists for ease of use.
 *
 * @component
 * @param {Object[]} tabs - Array of tab objects (see example)
 * @param {number} [initial=0] - (Optional) Index of the tab to show initially
 * @returns {JSX.Element}
 *
 * @example
 * const tabs = [
 *   { label: this.l("overview"), content: OverviewComponent }
 *    ...
 * ]
 *
 * return (
 *     <Tabs tabs={tabs} initial={0} />
 * )
 */

const useQuery = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const isValidTabParam = (tabParam, amountOfTabs) => {
  const parsedTab = parseInt(tabParam);
  if (isNaN(parsedTab) || parsedTab < 0 || parsedTab >= amountOfTabs) {
    return false;
  }
  return true;
};

const Tabs = forwardRef(
  ({ tabs = [], initial = 0, disableDeepLinking = false }, ref) => {
    const [currentTab, setCurrentTab] = useState(initial);
    const query = useQuery();
    const history = useHistory();

    //Temporary solution to avoid having to do whole-file rewrites.
    // DO NOT REPLICATE THIS SOLUTION ANYWHERE
    //----------------------------------------
    const updateTabStateFromParentDONOTUSE = (newTab) => {
      setCurrentTab(newTab);
    };

    useImperativeHandle(ref, () => ({
      updateTabStateFromParentDONOTUSE: updateTabStateFromParentDONOTUSE,
    }));
    //----------------------------------------

    useEffect(() => {
      if (!disableDeepLinking) {
        const queryTabParam = query.get("tab");
        if (isValidTabParam(queryTabParam, tabs?.length)) {
          if (currentTab !== +queryTabParam) {
            setCurrentTab(+queryTabParam);
          }
        } else {
          query.set("tab", initial);
          const newQueryString = "?" + query.toString();
          history.replace(newQueryString);
        }
      }
    }, [query.get("tab")]);

    const onTabChange = (e, newTab) => {
      if (disableDeepLinking) {
        setCurrentTab(newTab);
      } else {
        query.set("tab", newTab);
        const newQueryString = "?" + query.toString();
        history.push(newQueryString);
      }
    };

    return (
      <>
        <BaseTabs value={currentTab} onChange={onTabChange} aria-label="Tabs">
          {tabs.map(({ label, icon, dataTestId }, index) => (
            <Tab
              key={index}
              label={label}
              icon={icon}
              id={`tab-${index}`}
              aria-controls={`tabpanel-${index}`}
              {...(dataTestId && { 'data-testid': dataTestId })}
            />
          ))}
        </BaseTabs>
        <TabPanel
          value={currentTab}
          index={currentTab}
          aria-labelledby={`tab-${currentTab}`}
          id={`tabpanel-${currentTab}`}
        >
          {tabs[currentTab]?.content}
        </TabPanel>
      </>
    );
  }
);

export default Tabs;
